import { useEffect, useId, useState, type ReactNode, type RefObject } from 'react'
import { api } from '../servizi/api.ts'
import { Bottone, BottoneIcona, Etichetta, Scelta } from './base.tsx'
import { IconaChiudi } from './Icone.tsx'
import { etichettaEnte, type ComuneScelto, type EnteScelto, type Voce } from '../servizi/regole-anagrafica.ts'
import { opzioniSedi, type SedeElenco } from '../servizi/sedi.ts'
import {
  codiceDaVoce, lingueDalCatalogo, opzioniConAltreVoci, type Lingua, type LinguaCatalogo,
} from '../servizi/lingue.ts'

/**
 * Campi composti dell'anagrafica della Scheda Rev05 (corsia persona).
 *
 * Sono costruiti sui componenti di base.tsx e ne ripetono il markup (.campo, .campo-errore,
 * .campo-aiuto) dove base.tsx non ha un controllo adatto: caselle con voci disabilitate e con
 * l'errore del gruppo, ricerca di un comune o di un ente con i suggerimenti di un <datalist>,
 * scelta multipla da un elenco lungo, valore ricavato in sola lettura. Le classi nuove hanno il
 * prefisso `persona-` e stanno in stile-persona.css.
 */

function Nota({ id, errore, aiuto }: { id: string; errore?: string | null; aiuto?: ReactNode }) {
  if (errore) return <p className="campo-errore" id={`${id}-nota`} role="alert">{errore}</p>
  return aiuto ? <p className="campo-aiuto" id={`${id}-nota`}>{aiuto}</p> : null
}

/**
 * Gruppo di caselle da un catalogo, con legenda e messaggio d'errore del gruppo.
 * `disabilitata` toglie dalla scelta le voci escluse da un'altra (NESSUNO di questi servizi, D-18):
 * una voce gia' spuntata resta sempre modificabile, altrimenti un dato incoerente del vecchio
 * archivio non si potrebbe correggere.
 */
export function GruppoCaselle({
  legenda, voci, scelte, cambia, errore, aiuto, obbligatorio = false, disabilitata, inColonna = false,
}: {
  legenda: string
  voci: { codice: string; nome: ReactNode; classe_dato?: string }[]
  scelte: string[]
  cambia: (scelte: string[]) => void
  errore?: string | null
  aiuto?: ReactNode
  obbligatorio?: boolean
  disabilitata?: (codice: string) => boolean
  /** Una voce per riga: per le voci lunghe della Rev05 (servizi bancari, profilo di vulnerabilita'). */
  inColonna?: boolean
}) {
  const id = useId()
  return (
    <fieldset className={`gruppo-caselle persona-gruppo${errore ? ' campo-errato' : ''}`}
              aria-describedby={errore || aiuto ? `${id}-nota` : undefined}>
      <legend className={obbligatorio ? 'obbligatorio' : undefined}>{legenda}</legend>
      <div className={`scelte${inColonna ? ' persona-scelte-colonna' : ''}`}>
        {voci.map((v) => {
          const spuntata = scelte.includes(v.codice)
          return (
            <label key={v.codice} className={`scelta${v.classe_dato === 'riservato' ? ' riservata' : ''}`}>
              <input
                type="checkbox" checked={spuntata}
                disabled={!spuntata && disabilitata?.(v.codice)}
                aria-invalid={errore ? true : undefined}
                onChange={(e) => cambia(e.target.checked
                  ? [...scelte, v.codice]
                  : scelte.filter((c) => c !== v.codice))}
              />
              {v.nome}
            </label>
          )
        })}
      </div>
      <Nota id={id} errore={errore} aiuto={aiuto} />
    </fieldset>
  )
}

/**
 * Scelta singola a pulsanti di opzione (Esprimo / Non esprimo dei consensi, D-09): base.tsx non ha
 * un gruppo di opzioni. Nessuna opzione e' preselezionata, perche' un consenso non si presume: finche'
 * l'utente non sceglie, la dichiarazione resta senza esito. Le frecce spostano la scelta nel gruppo,
 * come per ogni gruppo di opzioni con lo stesso nome.
 */
export function GruppoScelta({
  legenda, descrizione, opzioni, valore, cambia, errore, aiuto, obbligatorio = false,
}: {
  legenda: ReactNode
  descrizione?: ReactNode
  opzioni: { valore: string; etichetta: string }[]
  valore: string
  cambia: (valore: string) => void
  errore?: string | null
  aiuto?: ReactNode
  obbligatorio?: boolean
}) {
  const id = useId()
  return (
    <fieldset className={`gruppo-caselle persona-gruppo${errore ? ' campo-errato' : ''}`}
              aria-describedby={errore || aiuto ? `${id}-nota` : undefined}>
      <legend className={obbligatorio ? 'obbligatorio' : undefined}>{legenda}</legend>
      {descrizione && <div className="persona-scelta-descrizione">{descrizione}</div>}
      <div className="scelte">
        {opzioni.map((o) => (
          <label key={o.valore} className="scelta">
            <input type="radio" name={id} value={o.valore} checked={valore === o.valore}
                   aria-invalid={errore ? true : undefined} onChange={() => cambia(o.valore)} />
            {o.etichetta}
          </label>
        ))}
      </div>
      <Nota id={id} errore={errore} aiuto={aiuto} />
    </fieldset>
  )
}

/** Spunta con il messaggio d'errore sotto: `Spunta` di base.tsx non ne ha, e il fuoco sul primo errore la deve trovare. */
export function SpuntaObbligatoria({
  etichetta, valore, cambia, errore,
}: { etichetta: ReactNode; valore: boolean; cambia: (v: boolean) => void; errore?: string | null }) {
  const id = useId()
  return (
    <div className={`spunta persona-spunta${errore ? ' campo-errato' : ''}`}>
      <input id={id} type="checkbox" checked={valore} onChange={(e) => cambia(e.target.checked)}
             aria-invalid={errore ? true : undefined} aria-describedby={errore ? `${id}-nota` : undefined} />
      <label htmlFor={id} className="obbligatorio">{etichetta}</label>
      {errore && <p className="campo-errore persona-spunta-errore" id={`${id}-nota`} role="alert">{errore}</p>}
    </div>
  )
}

type ComuneRiferimento = { codice_istat: string; nome: string; provincia_sigla: string | null; soppresso_il?: string | null }

/** Ricerca con suggerimenti: la richiesta parte dopo una breve pausa e da due caratteri. */
function useSuggerimenti<T>(testo: string, attiva: boolean, percorso: (termine: string) => string) {
  const [voci, setVoci] = useState<T[]>([])
  useEffect(() => {
    const termine = testo.trim()
    if (!attiva || termine.length < 2) return
    let vivo = true
    const attesa = setTimeout(() => {
      api.get<T[]>(percorso(termine)).then((r) => { if (vivo) setVoci(r) }).catch(() => undefined)
    }, 250)
    return () => { vivo = false; clearTimeout(attesa) }
    // `percorso` e' una funzione pura definita dal chiamante: cambia a ogni disegno senza cambiare
    // la richiesta, quindi resta fuori dalle dipendenze.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testo, attiva])
  return voci
}

/**
 * Comune a tendina con ricerca (D-12, D-13, D-20): si scrive il nome e si sceglie tra i
 * suggerimenti dell'elenco ISTAT. Il testo che non corrisponde a un suggerimento non ha codice e
 * il modulo lo segnala; la provincia si ricava dal comune scelto.
 */
export function SceltaComune({
  etichetta, valore, cambia, errore, aiuto, obbligatorio = false, disabilitato = false,
}: {
  etichetta: string
  valore: ComuneScelto
  cambia: (v: ComuneScelto) => void
  errore?: string | null
  aiuto?: ReactNode
  obbligatorio?: boolean
  /** Spento ma visibile: per esempio il comune di nascita di chi e' nato all'estero. */
  disabilitato?: boolean
}) {
  const id = useId()
  const suggeriti = useSuggerimenti<ComuneRiferimento>(valore.testo, !valore.codice,
    (t) => `/riferimenti/comuni?cerca=${encodeURIComponent(t)}&limite=20`)
  // Provincia davanti al nome (Osservazioni 3, punto 5).
  const etichettaDi = (c: ComuneRiferimento) =>
    `${c.provincia_sigla ? `${c.provincia_sigla} · ` : ''}${c.nome}${c.soppresso_il ? ' – soppresso' : ''}`
  return (
    <div className={`campo${errore ? ' campo-errato' : ''}`}>
      <label htmlFor={id}>
        {etichetta}
        {obbligatorio && <span className="campo-obbligatorio" aria-hidden="true"> *</span>}
      </label>
      <input
        id={id} list={`${id}-elenco`} value={valore.testo} autoComplete="off"
        placeholder="Nome del comune, o provincia: NA, Caserta…" required={obbligatorio} disabled={disabilitato}
        aria-invalid={errore ? true : undefined}
        aria-describedby={errore || aiuto ? `${id}-nota` : undefined}
        onChange={(e) => {
          const testo = e.target.value
          const scelto = suggeriti.find((c) => etichettaDi(c) === testo)
          cambia(scelto
            ? { codice: scelto.codice_istat, testo, provincia: scelto.provincia_sigla ?? '' }
            : { codice: '', testo, provincia: '' })
        }}
      />
      <datalist id={`${id}-elenco`}>
        {suggeriti.map((c) => <option key={c.codice_istat} value={etichettaDi(c)} />)}
      </datalist>
      <Nota id={id} errore={errore} aiuto={aiuto} />
    </div>
  )
}

type EnteRiferimento = {
  id: string; sigla: string | null; nome: string; categoria_ente: string | null; categoria_ente_nome: string | null
  /** Nomi degli enti uniti in questo come doppioni (475). */
  anche?: string | null
}

/**
 * Ente a tendina con ricerca sull'anagrafica enti (D-07, D-22). Legge GET
 * /persone/enti-selezionabili, che basta a chi crea o modifica schede anche senza ente.leggi.
 */
export function SceltaEnte({
  etichetta, valore, cambia, errore, aiuto, obbligatorio = false, disabilitato = false,
}: {
  etichetta: string
  valore: EnteScelto
  cambia: (v: EnteScelto) => void
  errore?: string | null
  aiuto?: ReactNode
  obbligatorio?: boolean
  /** Spento quando l'appartenenza e' gia' indicata altrove (sede CIDIS, N-2). */
  disabilitato?: boolean
}) {
  const id = useId()
  const suggeriti = useSuggerimenti<EnteRiferimento>(valore.testo, !valore.id,
    (t) => `/persone/enti-selezionabili?cerca=${encodeURIComponent(t)}&limite=20`)
  return (
    <div className={`campo${errore ? ' campo-errato' : ''}`}>
      <label htmlFor={id}>
        {etichetta}
        {obbligatorio && <span className="campo-obbligatorio" aria-hidden="true"> *</span>}
      </label>
      <input
        id={id} list={`${id}-elenco`} value={valore.testo} autoComplete="off"
        placeholder="Nome o sigla dell’ente" required={obbligatorio && !disabilitato}
        disabled={disabilitato}
        aria-invalid={errore ? true : undefined}
        aria-describedby={errore || aiuto ? `${id}-nota` : undefined}
        onChange={(e) => {
          const testo = e.target.value
          const scelto = suggeriti.find((x) => etichettaEnte(x) === testo)
          cambia(scelto
            ? { id: scelto.id, testo, tipologia: scelto.categoria_ente_nome ?? '' }
            : { id: '', testo, tipologia: '' })
        }}
      />
      <datalist id={`${id}-elenco`}>
        {/* Il vecchio nome nell'etichetta: cercando l'ente unito, il browser non nasconde quello tenuto. */}
        {suggeriti.map((x) => (
          <option key={x.id} value={etichettaEnte(x)}
                  label={x.anche ? `${etichettaEnte(x)} · anche: ${x.anche}` : undefined} />
        ))}
      </datalist>
      <Nota id={id} errore={errore} aiuto={aiuto} />
    </div>
  )
}

/**
 * Scelta multipla da un elenco lungo (lingue veicolari, D-14): si sceglie una voce e la si
 * aggiunge con il comando, cosi' scorrere la tendina con le frecce non aggiunge voci.
 * `massimo` riporta il limite dell'API: raggiunto, il comando si disattiva e l'aiuto lo dice,
 * invece di lasciare che il salvataggio fallisca senza indicare il limite. `disabilitato` spegne
 * tendina, comando e rimozione (per esempio quando «Tutte le sedi» prende il posto della scelta).
 */
export function SceltaMultipla({
  etichetta, voci, scelte, cambia, errore, aiuto, vuota, massimo, disabilitato = false,
}: {
  etichetta: string
  /** Con `altreVoci` (le lingue, 553) la voce compare anche sotto quei nomi; si sceglie sempre il codice. */
  voci: { codice: string; nome: string; altreVoci?: string[] }[]
  scelte: string[]
  cambia: (scelte: string[]) => void
  errore?: string | null
  aiuto?: ReactNode
  vuota: string
  massimo?: number
  disabilitato?: boolean
}) {
  const [daAggiungere, setDaAggiungere] = useState('')
  const nomeDi = (codice: string) => voci.find((v) => v.codice === codice)?.nome ?? codice
  const completa = massimo !== undefined && scelte.length >= massimo
  return (
    <div className={`persona-multipla${disabilitato ? ' persona-multipla-spenta' : ''}`}>
      <div className="persona-aggiunta">
        <Scelta etichetta={etichetta} valore={daAggiungere} cambia={setDaAggiungere} vuota={vuota}
                disabilitato={disabilitato}
                errore={errore} aiuto={completa ? `Al massimo ${massimo}: per aggiungerne un’altra se ne toglie una.` : aiuto}
                opzioni={opzioniConAltreVoci(voci, scelte)} />
        <Bottone variante="secondario" disabled={disabilitato || !daAggiungere || completa}
                 onClick={() => { cambia([...scelte, codiceDaVoce(daAggiungere)]); setDaAggiungere('') }}>
          Aggiungi
        </Bottone>
      </div>
      {scelte.length > 0 && (
        <ul className="persona-etichette" aria-label={`${etichetta}: voci scelte`}>
          {scelte.map((codice) => (
            <li key={codice}>
              <Etichetta tono="accento">
                {nomeDi(codice)}
                <BottoneIcona icona={<IconaChiudi />} etichetta={`Togli ${nomeDi(codice)}`}
                              disabled={disabilitato}
                              onClick={() => cambia(scelte.filter((c) => c !== codice))} />
              </Etichetta>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Valore ricavato dal sistema e non inseribile: provincia, tipologia ente, anno e mese (D-07, D-10, D-13). */
export function DatoDerivato({ etichetta, valore, aiuto }: { etichetta: string; valore: ReactNode; aiuto?: ReactNode }) {
  const id = useId()
  return (
    <div className="campo">
      <span className="persona-derivato-etichetta" id={id}>{etichetta}</span>
      <p className="persona-derivato-valore" role="status" aria-labelledby={id}>
        {valore || <span className="tenue">—</span>}
      </p>
      {aiuto && <p className="campo-aiuto">{aiuto}</p>}
    </div>
  )
}

/**
 * Dopo un invio non riuscito il fuoco va sul primo campo segnalato, cosi' da tastiera si arriva
 * all'errore senza scorrere il modulo. `tentativo` cresce a ogni invio: mentre si scrive il fuoco
 * non si sposta. Gli input di file e quelli nascosti non ricevono il fuoco: il campo della
 * scansione lo passa al comando che apre il selettore.
 */
export function useFuocoSulPrimoErrore(radice: RefObject<HTMLElement | null>, tentativo: number) {
  useEffect(() => {
    if (!tentativo) return
    radice.current?.querySelector<HTMLElement>(
      '.campo-errato input:not(:disabled):not([type="file"]):not([hidden]), .campo-errato select:not(:disabled), '
      + '.campo-errato textarea:not(:disabled), .campo-errato button:not(:disabled)',
    )?.focus()
  }, [radice, tentativo])
}

let lingueInCorso: Promise<Lingua[]> | null = null

/**
 * Catalogo delle lingue (ref.lingua), letto una volta per sessione del browser, con le voci in piu' (553): le
 * tendine le costruiscono con opzioniConAltreVoci e salvano il codice con codiceDaVoce (servizi/lingue.ts).
 */
export function useLingue(): Lingua[] {
  const [lingue, setLingue] = useState<Lingua[]>([])
  useEffect(() => {
    let vivo = true
    lingueInCorso ??= api.get<LinguaCatalogo[]>('/riferimenti/lingue').then(lingueDalCatalogo)
    lingueInCorso.then((l) => { if (vivo) setLingue(l) }).catch(() => { lingueInCorso = null })
    return () => { vivo = false }
  }, [])
  return lingue
}

/** Voci attive di un catalogo con i loro attributi, dal catalogo in sessione. */
export const vociDi = (cataloghi: Record<string, unknown[]> | null, nome: string): Voce[] =>
  (cataloghi?.[nome] ?? []) as Voce[]


/** Sede CIDIS a tendina: elenco corto, letto da GET /riferimenti/sedi. */
type SedeRiferimento = SedeElenco & { codice: string; attiva: boolean }

/**
 * Sede CIDIS di appartenenza (N-2), alternativa all'ente per chi rappresenta CIDIS stessa.
 *
 * L'elenco comprende le sedi chiuse solo se la scheda ne indica gia' una: una sede chiusa non si
 * sceglie piu', ma una scheda che la porta deve continuare a mostrarne il nome invece di svuotarsi
 * al primo salvataggio.
 */
export function SceltaSedeAppartenenza({
  valore, cambia, errore, disabilitato = false,
}: {
  valore: string
  cambia: (v: string) => void
  errore?: string | null
  disabilitato?: boolean
}) {
  const [sedi, setSedi] = useState<SedeRiferimento[]>([])
  useEffect(() => {
    api.get<SedeRiferimento[]>('/riferimenti/sedi?tutte=true').then(setSedi).catch(() => setSedi([]))
  }, [])
  const scegliibili = sedi.filter((s) => s.attiva || s.id === valore)
  return (
    <Scelta
      etichetta="Sede CIDIS di appartenenza"
      vuota="— nessuna: appartiene a un ente —"
      valore={valore}
      cambia={cambia}
      errore={errore}
      disabilitato={disabilitato}
      opzioni={opzioniSedi(scegliibili, { chiuse: true })}
      aiuto="Per chi fa parte di CIDIS: al posto dell’ente di appartenenza."
    />
  )
}
